import moment from 'moment';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { downloadXMLFile } from './xml';

/** Download bank file as Excel (.xlsx) */
export function downloadExcelBankFile(data: any, fileName: string) {
  const headers = [
    'name',
    'recipientEmail',
    'paymentReference',
    'receiverType',
    'amountCurrency',
    'amount',
    'sourceCurrency',
    'targetCurrency',
    'sortCode',
    'accountNumber',
  ];
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, '');
  XLSX.utils.sheet_add_aoa(worksheet, [headers]);
  XLSX.writeFile(workbook, `${fileName}.xlsx`, { compression: true });
}

/** Download bank file as plain text (.txt/.csv) */
export function downloadTxtFile(
  content: string,
  fileName: string,
  type: string,
) {
  const element = document.createElement('a');
  const file = new Blob([content], { type: 'text/plain' });
  element.href = URL.createObjectURL(file);
  element.download = fileName
    ? `${fileName}.${type === 'csvString' ? 'csv' : 'txt'}`
    : `payments.${type === 'csvString' ? 'csv' : 'txt'}`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(element.href);
}

/**
 * Download one or more bank-file parts.
 * A single part downloads as-is (.csv/.txt). Multiple parts are bundled into ONE
 * .zip and downloaded in a single click, so the browser doesn't block the 2nd+
 * file (Chrome's "download multiple files" permission) — this happens for Revolut
 * Ireland pay runs that split into a domestic + an international file.
 */
export async function downloadBankFiles(
  parts: { name: string; content: string }[],
  baseName: string,
  type: string,
) {
  const files = parts.filter((part) => part.content);
  if (!files.length) return;

  if (files.length === 1) {
    downloadTxtFile(files[0].content, files[0].name, type);
    return;
  }

  const ext = type === 'csvString' ? 'csv' : 'txt';
  const zip = new JSZip();
  files.forEach((file) => zip.file(`${file.name}.${ext}`, file.content));
  const blob = await zip.generateAsync({ type: 'blob' });

  const element = document.createElement('a');
  element.href = URL.createObjectURL(blob);
  element.download = `${baseName}.zip`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(element.href);
}

/**
 * Process generatePaymentFile API response and trigger file download.
 * Supports url, excel, xml, txt/csv formats.
 * Returns true if download succeeded.
 */
export async function exportBankFile(
  res: any,
  bankName?: string,
): Promise<boolean> {
  if (!res?.success) return false;

  const fileName = `${bankName || 'Bank'}_${moment().format('DD-MM-YYYY')}`;
  if (res.type === 'url') {
    window.open(res.data?.string || res.data?.downloadData, '_blank');
  } else if (res.type === 'excel') {
    downloadExcelBankFile(res.data?.downloadData, fileName);
  } else if (res.type === 'xml') {
    downloadXMLFile(
      res.data?.string || '',
      res.data?.filename || `${fileName}.xml`,
    );
  } else {
    await downloadBankFiles(
      [
        { name: fileName, content: res.data?.string || '' },
        { name: `Ireland_${fileName}`, content: res.data?.secondString || '' },
      ],
      fileName,
      res.type,
    );
  }
  return true;
}
